import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog as MuiDialog,
} from '@mui/material';
import { ReverseGeocodingMap, SearchAddress } from '@components';
import { ButtonV2, IconButtonV2, Text } from 'components/common/ui';
import { servicePointsServices } from '@services';
import { showError } from '@utils';
import { useDevice, useServicePoints } from '@hooks';

import CloseIcon from '@mui/icons-material/Close';

import EditIcon from '@mui/icons-material/Edit';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DEFAULT_LAT, DEFAULT_LNG } from '@constants';
import styles from './addServicePointAddressModal.style';
import validationSchema from './validationSchema';
import { AddressForm } from './AddressForm';
import { getFullAddress } from './utils';

const setAddress = (servicePoint) => {
  return servicePoint?.addresses && !!servicePoint?.addresses?.length
    ? 'Edit Address'
    : 'Add Address';
};

const INITIAL_CENTER = {
  lat: DEFAULT_LAT,
  lng: DEFAULT_LNG,
};

const AddServicePointAddressModal = ({
  servicePoint,
  getServicePoint,
  dataLoading,
  iconOnly,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [center, setCenter] = useState(INITIAL_CENTER);
  const [suggestion, setSuggestion] = useState('');
  const [addressForm, setAddressForm] = useState({});
  const { invalidateServicePoints } = useServicePoints({ enabled: false });
  const resolver = yupResolver(validationSchema);
  const methods = useForm({
    resolver,
    defaultValues: null,
  });
  const _toggleModal = () => {
    setOpen(!open);
  };

  const _onMove = (address) => {
    const fullAddress = getFullAddress(address);

    setSuggestion(fullAddress);

    setCenter({
      lat: address.center.lat,
      lng: address.center.lng,
    });

    delete address?.center;

    setAddressForm(address);
  };

  const { isMobile } = useDevice();

  const _handleAddressChange = (address) => {
    if (address) {
      setAddressForm(address);

      const fullAddress = getFullAddress(address);

      setSuggestion(fullAddress);

      setCenter({
        lat: address.latitude,
        lng: address.longitude,
      });
    }
  };

  const _onSubmit = async (form) => {
    try {
      setLoading(true);
      form.latitude = center?.lat;
      form.longitude = center?.lng;
      delete form?.updatedAt;
      delete form?.createdAt;
      await servicePointsServices.addServicePointAddress(
        servicePoint?.id,
        form
      );
      invalidateServicePoints();
      await getServicePoint();
      _toggleModal();
      return form;
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && servicePoint?.addresses?.length) {
      const address = servicePoint?.addresses[0];
      setAddressForm(address);
      setCenter({
        lat: address.latitude,
        lng: address.longitude,
      });
    }
  }, [open, setCenter, methods, servicePoint]);

  return (
    <>
      {!iconOnly && (
        <ButtonV2 variant="secondary" onClick={_toggleModal} width="100%">
          {setAddress(servicePoint)}
        </ButtonV2>
      )}
      {iconOnly && (
        <IconButtonV2
          variant="text"
          disabled={dataLoading}
          onClick={_toggleModal}
          styles={styles.editButton}
        >
          <EditIcon data-testid="EditIcon" fontSize="small" />
        </IconButtonV2>
      )}
      <MuiDialog
        open={open}
        onClose={_toggleModal}
        scroll="paper"
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle id="scroll-dialog-title">
          Edit Address
          <IconButtonV2
            variant="text"
            aria-label="close"
            onClick={_toggleModal}
            disabled={loading}
            styles={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon data-testid="CloseIcon" />
          </IconButtonV2>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            gap: '16px',
            flexDirection: isMobile ? 'column' : 'row',
          }}
          dividers
        >
          <Box sx={styles.mapContainer}>
            <SearchAddress onChange={_handleAddressChange} />
            <ReverseGeocodingMap center={center} onMove={_onMove} />
            {suggestion && (
              <Text sx={styles.suggestion}>
                <span style={{ fontWeight: 'bold' }}>
                  The center of the map is:{' '}
                </span>
                {suggestion}
              </Text>
            )}
          </Box>

          <Box sx={styles.formContainer}>
            <FormProvider {...methods}>
              <AddressForm onSubmit={_onSubmit} defaultValues={addressForm} />
            </FormProvider>
          </Box>

          {loading && (
            <Box
              sx={{
                width: '100%',
                height: '88%',
                top: '40px',
                left: '0px',
                opacity: 0.9,
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <ButtonV2
            variant="secondary"
            onClick={_toggleModal}
            disabled={loading}
          >
            Cancel
          </ButtonV2>
          <ButtonV2 type="submit" form="edit-address-form" loading={loading}>
            Ok
          </ButtonV2>
        </DialogActions>
      </MuiDialog>
    </>
  );
};

const memoizedAddServicePointAddressModal = React.memo(
  AddServicePointAddressModal
);
export { memoizedAddServicePointAddressModal as AddServicePointAddressModal };

export default memoizedAddServicePointAddressModal;
